import { useEffect, useState } from 'react';

export interface MenuIconProps {
  /** Asset name under /icons/menu/{name}.webp (+ {name}-poster.webp). */
  name: string;
  /** Fixed render size in px. Ignored when `fill` is set. */
  size?: number;
  /** Fill the parent chip/slot: 100% width/height, cover, and inherit the
   *  slot's border-radius so the existing rounded masking applies. */
  fill?: boolean;
  /** Accessible alt text. Pass '' for purely decorative placements. */
  alt: string;
  className?: string;
}

/**
 * Animated menu icon — the EvieOrb/TokenSpin img-swap pattern applied to the
 * fifteen-icon menu family. Renders the animated WebP and swaps to the static
 * poster frame under `prefers-reduced-motion: reduce` (live-updates on
 * preference change). Deliberately NOT a Lottie slot: these are brand raster
 * renders, so they ride the WebP path (loops natively, no <video> autoplay
 * quirks, no JSON fetch).
 *
 * Assets live at apps/web/public/icons/menu/:
 *   /icons/menu/{name}.webp          animated, loops natively
 *   /icons/menu/{name}-poster.webp   static first frame (reduced-motion)
 *
 * `fill` drops the icon INTO an existing chip/slot (Profile category chips,
 * ListRow icon wells, empty-state circles): the img spans the slot, covers,
 * and inherits its rounding — the slot's own styling is never touched.
 */
export function MenuIcon({ name, size = 30, fill = false, alt, className }: MenuIconProps) {
  // Default to "no preference" so first paint renders the animation; the
  // effect flips to the poster only when the user prefers reduced motion.
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mql.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return (
    <img
      src={reducedMotion ? `/icons/menu/${name}-poster.webp` : `/icons/menu/${name}.webp`}
      alt={alt}
      draggable={false}
      className={className}
      {...(fill ? {} : { width: size, height: size })}
      style={
        fill
          ? {
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 'inherit',
            }
          : {
              display: 'block',
              width: size,
              height: size,
              objectFit: 'cover',
              // Small standalone placements round softly; slot placements
              // should use `fill` and inherit the slot's masking instead.
              borderRadius: Math.round(size * 0.25),
              flexShrink: 0,
            }
      }
    />
  );
}
